import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Menu, Search, ShoppingBag, User, X, Shield, Package, LogOut, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useStore } from "../../context/StoreContext";
import { catUrl } from "../../lib/categoryUrl";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";

export default function Header() {
  const { settings, categories, customer, cart, setCartOpen, logout } = useStore();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const [expandedSub, setExpandedSub] = useState(null);
  const [openMega, setOpenMega] = useState(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [menuLeft, setMenuLeft] = useState(0);
  const navRef = useRef(null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const isMega = (c) => c.children?.length > 8 || c.children?.some((ch) => ch.children?.length);
  const openCat = categories.find((c) => c.category_id === openMega);
  const megaCat = openCat && isMega(openCat) ? openCat : null;
  const classicCat = openCat && !isMega(openCat) ? openCat : null;

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  const scrollNav = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: "smooth" });
  };

  const calcMenuLeft = (e) => {
    const navEl = navRef.current;
    if (!navEl) return 0;
    const left = e.currentTarget.getBoundingClientRect().left - navEl.getBoundingClientRect().left;
    return Math.min(Math.max(0, left), Math.max(0, navEl.clientWidth - 240));
  };

  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [categories]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/ara?q=${encodeURIComponent(q.trim())}`);
      setMobileOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-40">
      <div className="bg-[#121214] text-[#FAF9F5] text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between gap-4">
          <p className="font-mono-ed tracking-[0.18em] uppercase truncate" data-testid="announcement-bar">
            {settings?.announcement_bar || "BIGELDİ"}
          </p>
          <div className="hidden sm:flex items-center gap-4 shrink-0">
            <Link to="/siparis-takip" className="hover:text-amber-400 transition-colors" data-testid="header-track-link">Sipariş Takibi</Link>
            <Link to="/admin" className="hover:text-amber-400 transition-colors flex items-center gap-1" data-testid="header-admin-link">
              <Shield size={12} /> Yönetim
            </Link>
          </div>
        </div>
      </div>

      <div className="bg-[#FAF9F5]/85 backdrop-blur-xl border-b border-[#E6E4DD]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 sm:h-20 flex items-center gap-3 sm:gap-8">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="Menü">
              <Menu size={22} />
            </button>

            <Link to="/" data-testid="navbar-brand-logo">
              <img src="/assets/bigeldi-logo.png" alt="BIGELDİ" className="h-9 sm:h-10 w-auto object-contain" />
            </Link>

            <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xl">
              <div className="relative w-full group">
                <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 group-focus-within:text-amber-600 transition-colors" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ürün, marka veya kategori ara..."
                  className="w-full h-11 pl-11 pr-4 bg-white border border-[#E6E4DD] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-amber-600/30 focus:border-amber-600 transition-all"
                  data-testid="navbar-search-input"
                />
              </div>
            </form>

            <div className="flex items-center gap-1 sm:gap-2 ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-[#F4F2EB] transition-colors" data-testid="navbar-account-button">
                    <User size={19} />
                    <span className="hidden sm:block text-sm font-medium max-w-[90px] truncate">
                      {customer ? customer.name?.split(" ")[0] : "Hesabım"}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {customer ? (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/hesabim")} data-testid="account-menu-profile">
                        <User size={15} className="mr-2" /> Hesabım
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/hesabim?tab=siparisler")} data-testid="account-menu-orders">
                        <Package size={15} className="mr-2" /> Siparişlerim
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="account-menu-logout">
                        <LogOut size={15} className="mr-2" /> Çıkış Yap
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/giris")} data-testid="account-menu-login">Giriş Yap</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/kayit")} data-testid="account-menu-register">Üye Ol</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <button
                onClick={() => setCartOpen(true)}
                className="relative flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-[#121214] text-[#FAF9F5] hover:bg-amber-600 transition-colors"
                data-testid="navbar-cart-button"
              >
                <ShoppingBag size={18} />
                <span className="hidden sm:block text-sm font-medium">Sepet</span>
                {cart?.item_count > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-rose-600 text-white text-[11px] font-bold flex items-center justify-center" data-testid="cart-count-badge">
                    {cart.item_count}
                  </span>
                )}
              </button>
            </div>
          </div>

          <nav ref={navRef} className="relative hidden lg:flex items-center" data-testid="category-nav" onMouseLeave={() => setOpenMega(null)}>
            {canLeft && (
              <button onClick={() => scrollNav(-1)} className="shrink-0 h-11 w-8 flex items-center justify-center text-neutral-500 hover:text-amber-700 transition-colors" data-testid="nav-scroll-left" aria-label="Sola kaydır">
                <ChevronLeft size={18} />
              </button>
            )}
            <div ref={scrollRef} onScroll={updateArrows} className="flex-1 flex items-center flex-nowrap gap-6 xl:gap-7 h-11 text-[13px] font-medium tracking-wide overflow-x-auto no-scrollbar">
            {categories.map((c) => (
              c.children?.length ? (
                <div key={c.category_id} className="h-full flex items-center shrink-0 whitespace-nowrap"
                  onMouseEnter={(e) => { setOpenMega(c.category_id); if (!isMega(c)) setMenuLeft(calcMenuLeft(e)); }}>
                  <Link to={catUrl(c)} className={`relative py-2 transition-colors flex items-center gap-1 whitespace-nowrap after:absolute after:bottom-1 after:left-0 after:h-[2px] after:bg-amber-600 after:transition-all hover:text-amber-700 ${openMega === c.category_id ? "text-amber-700 after:w-full" : "after:w-0"}`} data-testid={`nav-category-${c.slug}`}>
                    {c.name}
                    <ChevronDown size={13} className={`text-neutral-400 transition-transform ${openMega === c.category_id ? "rotate-180" : ""}`} />
                  </Link>
                </div>
              ) : (
                <Link key={c.category_id} to={catUrl(c)} onMouseEnter={() => setOpenMega(null)} className="relative py-2 whitespace-nowrap shrink-0 hover:text-amber-700 transition-colors after:absolute after:bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-amber-600 hover:after:w-full after:transition-all" data-testid={`nav-category-${c.slug}`}>
                  {c.name}
                </Link>
              )
            ))}
            <a href="https://dugun.bigeldi.com/" target="_blank" rel="noopener noreferrer" onMouseEnter={() => setOpenMega(null)}
              className="relative py-2 whitespace-nowrap shrink-0 text-amber-700 font-semibold hover:text-amber-500 transition-colors flex items-center gap-1.5 after:absolute after:bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-amber-600 hover:after:w-full after:transition-all"
              data-testid="nav-dijital-davetiye">
              Dijital Davetiye
              <Heart size={12} className="text-amber-600" />
            </a>
            <Link to="/ara" onMouseEnter={() => setOpenMega(null)} className="py-2 whitespace-nowrap shrink-0 text-amber-700 font-semibold" data-testid="nav-all-products">Tüm Ürünler</Link>
            </div>
            {canRight && (
              <button onClick={() => scrollNav(1)} className="shrink-0 h-11 w-8 flex items-center justify-center text-neutral-500 hover:text-amber-700 transition-colors" data-testid="nav-scroll-right" aria-label="Sağa kaydır">
                <ChevronRight size={18} />
              </button>
            )}

            {megaCat && (
              <div className="absolute left-0 right-0 top-full z-50 pt-1" data-testid={`mega-menu-${megaCat.slug}`}>
                <div className="bg-white border border-[#E6E4DD] rounded-2xl shadow-xl shadow-black/10 p-6 max-h-[70vh] overflow-y-auto">
                  {megaCat.children.some((ch) => ch.children?.length) ? (
                    <div className="grid grid-cols-3 xl:grid-cols-5 gap-6">
                      {megaCat.children.map((group) => (
                        <div key={group.category_id}>
                          <Link to={catUrl(group)} className="block font-display font-bold text-sm text-[#121214] hover:text-amber-700 mb-2 uppercase tracking-wide" data-testid={`mega-group-${group.slug}`}>
                            {group.name}
                          </Link>
                          <div className="space-y-1">
                            {(group.children || []).map((leaf) => (
                              <Link key={leaf.category_id} to={catUrl(leaf)}
                                className="block text-[13px] text-neutral-500 hover:text-amber-700 transition-colors"
                                data-testid={`mega-item-${leaf.slug}`}>
                                {leaf.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 xl:grid-cols-5 gap-x-6 gap-y-1">
                      {megaCat.children.map((ch) => (
                        <Link key={ch.category_id} to={catUrl(ch, megaCat.slug)}
                          className="block py-1.5 text-[13px] text-neutral-600 hover:text-amber-700 transition-colors"
                          data-testid={`mega-item-${ch.slug}`}>
                          {ch.name}
                        </Link>
                      ))}
                    </div>
                  )}
                  <div className="mt-5 pt-4 border-t border-[#F0EEE7]">
                    <Link to={catUrl(megaCat)} className="text-[13px] font-semibold text-amber-700 hover:text-amber-800" data-testid={`mega-all-${megaCat.slug}`}>
                      Tüm {megaCat.name} Ürünleri →
                    </Link>
                  </div>
                </div>
              </div>
            )}
            {classicCat && (
              <div className="absolute top-full z-50 pt-1" style={{ left: menuLeft }} data-testid={`dropdown-${classicCat.slug}`}>
                <div className="bg-white border border-[#E6E4DD] rounded-2xl shadow-xl shadow-black/5 py-2 min-w-[210px] overflow-hidden">
                  {classicCat.children.map((ch) => (
                    <Link key={ch.category_id} to={catUrl(ch, classicCat.slug)}
                      className="block px-5 py-2.5 text-[13px] text-neutral-600 hover:text-amber-700 hover:bg-[#FAF9F5] transition-colors whitespace-nowrap"
                      data-testid={`nav-subcategory-${ch.slug}`}>
                      {ch.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </nav>
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-[#FAF9F5] p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <img src="/assets/bigeldi-logo.png" alt="BIGELDİ" className="h-9 w-auto object-contain" />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" aria-label="Kapat"><X size={22} /></button>
            </div>
            <form onSubmit={submitSearch} className="mb-6">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara..." className="w-full h-11 px-4 bg-white border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600" data-testid="mobile-search-input" />
            </form>
            <div className="space-y-1">
              {categories.map((c) => (
                <div key={c.category_id}>
                  {c.children?.length ? (
                    <>
                      <div className="flex items-center justify-between">
                        <Link to={catUrl(c)} onClick={() => setMobileOpen(false)} className="block py-2.5 font-display font-bold text-lg flex-1" data-testid={`mobile-nav-${c.slug}`}>{c.name}</Link>
                        <button onClick={() => setExpandedCat(expandedCat === c.category_id ? null : c.category_id)}
                          className="p-2 text-neutral-500" aria-label="Alt kategoriler" data-testid={`mobile-accordion-${c.slug}`}>
                          <ChevronDown size={18} className={`transition-transform ${expandedCat === c.category_id ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                      {expandedCat === c.category_id && (
                        <div className="pb-2">
                          {c.children.map((ch) => (
                            <div key={ch.category_id}>
                              {ch.children?.length ? (
                                <>
                                  <div className="flex items-center justify-between pl-4">
                                    <Link to={catUrl(ch, c.slug)} onClick={() => setMobileOpen(false)}
                                      className="block py-2 text-sm font-semibold text-neutral-600 hover:text-amber-700 flex-1"
                                      data-testid={`mobile-nav-${ch.slug}`}>{ch.name}</Link>
                                    <button onClick={() => setExpandedSub(expandedSub === ch.category_id ? null : ch.category_id)}
                                      className="p-2 text-neutral-400" aria-label="Alt başlıklar" data-testid={`mobile-accordion-${ch.slug}`}>
                                      <ChevronDown size={15} className={`transition-transform ${expandedSub === ch.category_id ? "rotate-180" : ""}`} />
                                    </button>
                                  </div>
                                  {expandedSub === ch.category_id && (
                                    <div className="pb-1">
                                      {ch.children.map((leaf) => (
                                        <Link key={leaf.category_id} to={catUrl(leaf)} onClick={() => setMobileOpen(false)}
                                          className="block py-1.5 pl-8 text-[13px] text-neutral-500 hover:text-amber-700"
                                          data-testid={`mobile-nav-${leaf.slug}`}>{leaf.name}</Link>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <Link to={catUrl(ch, c.slug)} onClick={() => setMobileOpen(false)}
                                  className="block py-2 pl-4 text-sm text-neutral-500 hover:text-amber-700"
                                  data-testid={`mobile-nav-${ch.slug}`}>{ch.name}</Link>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link to={catUrl(c)} onClick={() => setMobileOpen(false)} className="block py-2.5 font-display font-bold text-lg" data-testid={`mobile-nav-${c.slug}`}>{c.name}</Link>
                  )}
                </div>
              ))}
              <a href="https://dugun.bigeldi.com/" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 py-2.5 font-display font-bold text-lg text-amber-700"
                data-testid="mobile-nav-dijital-davetiye">
                Dijital Davetiye <Heart size={14} />
              </a>
              <Link to="/ara" onClick={() => setMobileOpen(false)} className="block py-2.5 font-display font-bold text-lg text-amber-700" data-testid="mobile-nav-all-products">Tüm Ürünler</Link>
            </div>
            <div className="mt-8 pt-6 border-t border-[#E6E4DD] space-y-2 text-sm">
              <Link to="/siparis-takip" onClick={() => setMobileOpen(false)} className="block py-2">Sipariş Takibi</Link>
              <Link to={customer ? "/hesabim" : "/giris"} onClick={() => setMobileOpen(false)} className="block py-2">{customer ? "Hesabım" : "Giriş Yap / Üye Ol"}</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
